/**
 * ModelPicker — composant MUI à afficher avant une action IA.
 * Laisse l'utilisateur choisir entre tier économique (1 crédit par défaut)
 * et tier premium (10 crédits). Affiche le coût en temps réel pour la
 * feature passée en prop, récupéré côté backend via estimateCost.
 *
 * Usage :
 *   <ModelPicker
 *     feature="shopping_list"
 *     value={tier}
 *     onChange={setTier}
 *     planAllowsPremium={accessPlan === 'premium' || accessPlan === 'standard'}
 *   />
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
} from '@mui/material'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'

import { estimateCost } from './billingAPI'

const TIERS = [
  {
    value: 'economic',
    label: 'Économique',
    description: 'Plus rapide, idéal pour les actions simples.',
    icon: BoltRoundedIcon,
  },
  {
    value: 'premium',
    label: 'Premium',
    description: 'Modèle avancé, idéal pour les réponses nuancées.',
    icon: AutoAwesomeRoundedIcon,
  },
]

export default function ModelPicker({
  feature,
  value = 'economic',
  onChange,
  planAllowsPremium = true,
  disabled = false,
}) {
  const [costs, setCosts] = useState({ economic: 1, premium: 10 })

  useEffect(() => {
    let cancelled = false
    async function refresh() {
      const [eco, pro] = await Promise.all([
        estimateCost({ feature, tier: 'economic' }),
        estimateCost({ feature, tier: 'premium' }),
      ])
      if (cancelled) return
      const next = { economic: 1, premium: 10 }
      if (eco?.status === 200 && typeof eco.data?.cost === 'number') next.economic = eco.data.cost
      if (pro?.status === 200 && typeof pro.data?.cost === 'number') next.premium = pro.data.cost
      setCosts(next)
    }
    if (feature) refresh()
    return () => {
      cancelled = true
    }
  }, [feature])

  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: '#001B42', fontWeight: 600, mb: 1.5 }}>
        Modèle IA
      </Typography>
      <RadioGroup
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        sx={{ gap: 1.5 }}
      >
        {TIERS.map((tier) => {
          const allowed = tier.value === 'economic' || planAllowsPremium
          const Icon = tier.icon
          const isChecked = value === tier.value
          return (
            <FormControlLabel
              key={tier.value}
              value={tier.value}
              disabled={disabled || !allowed}
              control={<Radio sx={{ ml: 1 }} />}
              sx={{
                m: 0,
                border: isChecked ? '1.5px solid #001B42' : '1px solid #e0e0e0',
                borderRadius: '8px',
                py: 1,
                pr: 2,
                bgcolor: isChecked ? 'rgba(0, 27, 66, 0.04)' : 'transparent',
                transition: 'border-color .15s ease, background-color .15s ease',
                '.MuiTypography-root': { width: '100%' },
                opacity: !allowed ? 0.55 : 1,
              }}
              label={
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Icon sx={{ color: '#001B42', opacity: 0.8 }} />
                  <Box flex={1}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#001B42' }}>
                      {tier.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'gray' }}>
                      {!allowed
                        ? 'Réservé aux offres Standard et Premium'
                        : tier.description}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={`${costs[tier.value]} crédit${costs[tier.value] > 1 ? 's' : ''}`}
                    sx={{
                      bgcolor: isChecked ? '#001B42' : '#EEF1F5',
                      color: isChecked ? 'white' : '#001B42',
                      fontWeight: 600,
                    }}
                  />
                </Box>
              }
            />
          )
        })}
      </RadioGroup>
    </Box>
  )
}
